import { Fragment, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Dropdown } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { getUserDisbursements, getUserPayouts, getUserStatement, exportUserStatement } from '../../services/LedgerService'
import useGuidedTour from '../../hooks/useGuidedTour'
import { MY_ACTIVITY_TOUR_ID, buildMyActivityTourSteps } from './MyActivity.tour'
import { SECURITY_OUTRO_TOUR_ID } from '../../hooks/faqOutroSteps'
import CounterpartyReveal from './CounterpartyReveal'
import { formatReceiverPartyForDisplay } from '../../utils/phoneFormat'
import { scrollToPageTop } from '../../utils/scrollToPageTop'

const TABS = {
    DISBURSEMENTS: 'disbursements',
    PAYOUTS:       'payouts',
    STATEMENT:     'statement',
}

const EMPTY_DISBURSEMENT_FILTERS = { date_from: '', date_to: '', search: '' }
const EMPTY_PAYOUT_FILTERS       = { date_from: '', date_to: '', status: '', search: '' }
const EMPTY_STATEMENT_FILTERS    = { date_from: '', date_to: '', type: '' }

const stripEmptyFilters = (filters) =>
    Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== ''))

const formatAmount = (amount) =>
    `KES ${Number(amount).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`

const formatDate = (dateString) =>
    new Date(dateString).toLocaleString('en-KE', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    })

const describeStatementEntry = (entry) => {
    const description = entry.description || ''
    const receiverParty = entry.receiver_party
    if (!receiverParty || description.includes(' - ')) return description
    const parts = receiverParty.split(' - ')
    const name = parts.length > 1 ? parts.slice(1).join(' - ').trim() : receiverParty.trim()
    return name ? `${description} - ${name}` : description
}

const payoutStatusBadge = (resultCode) => {
    if (resultCode === '0') return { cls: 'badge-success', label: 'Successful' }
    if (resultCode == null) return { cls: 'badge-warning', label: 'Pending' }
    return { cls: 'badge-danger', label: 'Failed' }
}

const TableLoadingRow = ({ colSpan }) => (
    <tr>
        <td colSpan={colSpan} className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading…</span>
            </div>
        </td>
    </tr>
)

const TableErrorRow = ({ colSpan }) => (
    <tr>
        <td colSpan={colSpan} className="text-center py-4 text-danger">
            Failed to load records. Please try again.
        </td>
    </tr>
)

const TableEmptyRow = ({ colSpan, message }) => (
    <tr>
        <td colSpan={colSpan} className="text-center py-4">{message}</td>
    </tr>
)

const PaginationBar = ({ page, lastPage, isLoading, onPrev, onNext }) => {
    if (lastPage <= 1) return null
    return (
        <div className="d-flex justify-content-center align-items-center gap-3 px-3 py-2 border-top">
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page <= 1 || isLoading}
                onClick={onPrev}
                aria-label="Previous page"
            >
                <i className="fa fa-angle-left me-1" />
                Prev
            </button>
            <span className="text-muted small">Page {page} of {lastPage}</span>
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page >= lastPage || isLoading}
                onClick={onNext}
                aria-label="Next page"
            >
                Next
                <i className="fa fa-angle-right ms-1" />
            </button>
        </div>
    )
}

const FilterBar = ({ filters, showStatus, showType, onChange, onClear, onExport, hasData }) => {
    const hasActiveFilter = Object.values(filters).some(v => v !== '')

    const statusOptions = [
        { value: '',           label: 'All statuses' },
        { value: 'successful', label: 'Successful' },
        { value: 'pending',    label: 'Pending' },
        { value: 'failed',     label: 'Failed' },
    ]
    const typeOptions = [
        { value: '',              label: 'All types' },
        { value: 'disbursement',  label: 'Disbursement' },
        { value: 'payout',        label: 'Payout' },
    ]
    const currentStatusLabel = statusOptions.find(o => o.value === filters.status)?.label ?? 'All statuses'
    const currentTypeLabel   = typeOptions.find(o => o.value === filters.type)?.label ?? 'All types'
    const fromLabel = filters.date_from ? `From ${filters.date_from}` : 'From date'
    const toLabel   = filters.date_to   ? `To ${filters.date_to}`     : 'To date'

    return (
        <div className="mb-3">
            {'search' in filters && (
                <input
                    type="text"
                    className="form-control mb-2"
                    placeholder="Search"
                    value={filters.search}
                    onChange={e => onChange('search', e.target.value)}
                />
            )}
            <div className="d-flex flex-wrap gap-2 align-items-center">
                <Dropdown autoClose="outside">
                    <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center" style={{ minWidth: '140px' }}>
                        <span className="flex-grow-1 text-start">{fromLabel}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="p-2">
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={filters.date_from}
                            onChange={e => onChange('date_from', e.target.value)}
                        />
                    </Dropdown.Menu>
                </Dropdown>
                <Dropdown autoClose="outside">
                    <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center" style={{ minWidth: '140px' }}>
                        <span className="flex-grow-1 text-start">{toLabel}</span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu className="p-2">
                        <input
                            type="date"
                            className="form-control form-control-sm"
                            value={filters.date_to}
                            onChange={e => onChange('date_to', e.target.value)}
                        />
                    </Dropdown.Menu>
                </Dropdown>
                {showStatus && (
                    <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center" style={{ minWidth: '140px' }}>
                            <span className="flex-grow-1 text-start">{currentStatusLabel}</span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {statusOptions.map(option => (
                                <Dropdown.Item
                                    key={option.value || 'all'}
                                    active={filters.status === option.value}
                                    onClick={() => onChange('status', option.value)}
                                >
                                    {option.label}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )}
                {showType && (
                    <Dropdown>
                        <Dropdown.Toggle variant="outline-secondary" size="sm" className="d-inline-flex align-items-center" style={{ minWidth: '140px' }}>
                            <span className="flex-grow-1 text-start">{currentTypeLabel}</span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {typeOptions.map(option => (
                                <Dropdown.Item
                                    key={option.value || 'all'}
                                    active={filters.type === option.value}
                                    onClick={() => onChange('type', option.value)}
                                >
                                    {option.label}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )}
                {hasActiveFilter && (
                    <button
                        type="button"
                        className="btn btn-sm btn-link text-decoration-none p-0 ms-1"
                        onClick={onClear}
                    >
                        Clear
                    </button>
                )}
                {onExport && (
                    <button
                        type="button"
                        className="btn btn-sm btn-link text-decoration-none p-0 ms-auto"
                        disabled={!hasData}
                        onClick={onExport}
                    >
                        <i className="fa fa-download me-1" />
                        Export CSV
                    </button>
                )}
            </div>
        </div>
    )
}


const MyActivity = () => {
    const userId = useSelector((state) => state.auth.authUser?.id)
    const hasTransactionPin = useSelector((state) => state.auth.authUser?.has_transaction_pin)
    const completedTourIds = useSelector((state) => state.auth.authUser?.completed_tour_ids ?? [])
    const navigate = useNavigate()
    const myActivityTourSteps = useMemo(
        () => buildMyActivityTourSteps({ navigate, hasTransactionPin, completedTourIds }),
        [navigate, hasTransactionPin, completedTourIds],
    )
    useGuidedTour(
        MY_ACTIVITY_TOUR_ID,
        myActivityTourSteps,
        { enabled: !!userId, alsoMarkCompleted: [SECURITY_OUTRO_TOUR_ID] },
    )

    const [searchParams] = useSearchParams()
    const initialTab = Object.values(TABS).includes(searchParams.get('tab')) ? searchParams.get('tab') : TABS.STATEMENT
    const [activeTab,            setActiveTab]            = useState(initialTab)
    const [disbursementsPage,    setDisbursementsPage]    = useState(1)
    const [payoutsPage,          setPayoutsPage]          = useState(1)
    const [statementPage,        setStatementPage]        = useState(1)
    const [disbursementsFilters, setDisbursementsFilters] = useState(EMPTY_DISBURSEMENT_FILTERS)
    const [payoutsFilters,       setPayoutsFilters]       = useState(EMPTY_PAYOUT_FILTERS)
    const [statementFilters,     setStatementFilters]     = useState(EMPTY_STATEMENT_FILTERS)

    const handleDisbursementsFilterChange = (key, value) => {
        setDisbursementsFilters(prev => ({ ...prev, [key]: value }))
        setDisbursementsPage(1)
    }

    const handlePayoutsFilterChange = (key, value) => {
        setPayoutsFilters(prev => ({ ...prev, [key]: value }))
        setPayoutsPage(1)
    }

    const handleStatementFilterChange = (key, value) => {
        setStatementFilters(prev => ({ ...prev, [key]: value }))
        setStatementPage(1)
    }

    const handleStatementExport = async () => {
        try {
            const response = await exportUserStatement(userId, stripEmptyFilters(statementFilters))
            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'text/csv' })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', 'wallet-statement.csv')
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        } catch (error) {
            const message = error?.response?.data?.message || 'Could not export the statement. Please try again.'
            toast.error(message)
        }
    }

    const disbursementsQuery = useQuery({
        queryKey: ['my-disbursements', userId, disbursementsPage, disbursementsFilters],
        queryFn:  () => getUserDisbursements(userId, disbursementsPage, stripEmptyFilters(disbursementsFilters)),
        placeholderData: keepPreviousData,
        staleTime: 0,
        enabled: !!userId && activeTab === TABS.DISBURSEMENTS,
    })

    const payoutsQuery = useQuery({
        queryKey: ['my-payouts', userId, payoutsPage, payoutsFilters],
        queryFn:  () => getUserPayouts(userId, payoutsPage, stripEmptyFilters(payoutsFilters)),
        placeholderData: keepPreviousData,
        staleTime: 0,
        enabled: !!userId && activeTab === TABS.PAYOUTS,
    })

    const statementQuery = useQuery({
        queryKey: ['my-statement', userId, statementPage, statementFilters],
        queryFn:  () => getUserStatement(userId, statementPage, stripEmptyFilters(statementFilters)),
        placeholderData: keepPreviousData,
        staleTime: 0,
        enabled: !!userId && activeTab === TABS.STATEMENT,
    })

    const disbursements     = disbursementsQuery.data?.data?.data ?? []
    const disbursementsLast = disbursementsQuery.data?.data?.meta?.last_page ?? 1

    const payouts     = payoutsQuery.data?.data?.data ?? []
    const payoutsLast = payoutsQuery.data?.data?.meta?.last_page ?? 1

    const statementData    = statementQuery.data?.data
    const statementEntries = statementData?.entries ?? []
    const statementLast    = statementData?.meta?.last_page ?? 1

    return (
        <Fragment>
            <div className="row">
                <div className="col-12">
                    <div className="card" data-tour="my-activity-card">
                        <div className="card-header">
                            <h4 className="card-title mb-0">My Activity</h4>
                        </div>
                        <div className="card-body">
                            <ul className="nav nav-tabs mb-3">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === TABS.STATEMENT ? 'active' : ''}`}
                                        onClick={() => setActiveTab(TABS.STATEMENT)}
                                        data-tour="my-activity-statement-tab"
                                    >
                                        My Statement
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === TABS.DISBURSEMENTS ? 'active' : ''}`}
                                        onClick={() => setActiveTab(TABS.DISBURSEMENTS)}
                                        data-tour="my-activity-disbursements-tab"
                                    >
                                        My Disbursements
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === TABS.PAYOUTS ? 'active' : ''}`}
                                        onClick={() => setActiveTab(TABS.PAYOUTS)}
                                        data-tour="my-activity-payouts-tab"
                                    >
                                        My Payouts
                                    </button>
                                </li>
                            </ul>

                            {activeTab === TABS.DISBURSEMENTS && (
                                <div>
                                    <FilterBar
                                        filters={disbursementsFilters}
                                        onChange={handleDisbursementsFilterChange}
                                        onClear={() => { setDisbursementsFilters(EMPTY_DISBURSEMENT_FILTERS); setDisbursementsPage(1) }}
                                    />
                                    <div>
                                        <table className="table verticle-middle mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                                            <thead className="thead-lg-only">
                                                <tr>
                                                    <th scope="col" style={{ width: '25%' }}>Amount</th>
                                                    <th scope="col" style={{ width: '35%' }}>Initiated by</th>
                                                    <th scope="col" style={{ width: '40%' }}>Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {disbursementsQuery.status === 'pending' ? (
                                                    <TableLoadingRow colSpan={3} />
                                                ) : disbursementsQuery.error ? (
                                                    <TableErrorRow colSpan={3} />
                                                ) : disbursements.length === 0 ? (
                                                    <TableEmptyRow colSpan={3} message="No disbursements recorded yet." />
                                                ) : (
                                                    disbursements.map((item, index) => (
                                                        <tr key={index}>
                                                            <td>
                                                                <div className="d-lg-none" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                                                    <div className="fw-semibold text-nowrap">
                                                                        {formatAmount(item.amount)}
                                                                    </div>
                                                                    <div className="text-muted small mt-1">
                                                                        From{' '}
                                                                        {item.initiated_by
                                                                            ? item.initiated_by.name
                                                                            : <span className="fst-italic">Unknown</span>}
                                                                    </div>
                                                                    <div className="text-muted small">{formatDate(item.created_at)}</div>
                                                                </div>
                                                                <span className="d-none d-lg-inline text-nowrap fw-semibold">
                                                                    {formatAmount(item.amount)}
                                                                </span>
                                                            </td>
                                                            <td className="d-none d-lg-table-cell" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                {item.initiated_by
                                                                    ? item.initiated_by.name
                                                                    : <span className="text-muted">-</span>}
                                                            </td>
                                                            <td className="d-none d-lg-table-cell text-nowrap">{formatDate(item.created_at)}</td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                        <PaginationBar
                                            page={disbursementsPage}
                                            lastPage={disbursementsLast}
                                            isLoading={disbursementsQuery.isFetching}
                                            onPrev={() => { setDisbursementsPage(p => p - 1); scrollToPageTop(); }}
                                            onNext={() => { setDisbursementsPage(p => p + 1); scrollToPageTop(); }}
                                        />
                                    </div>
                                </div>
                            )}

                            {activeTab === TABS.PAYOUTS && (
                                <div>
                                    <FilterBar
                                        filters={payoutsFilters}
                                        showStatus={true}
                                        onChange={handlePayoutsFilterChange}
                                        onClear={() => { setPayoutsFilters(EMPTY_PAYOUT_FILTERS); setPayoutsPage(1) }}
                                    />
                                    <div>
                                        <table className="table verticle-middle mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                                            <thead className="thead-lg-only">
                                                <tr>
                                                    <th scope="col" style={{ width: '18%' }}>Amount</th>
                                                    <th scope="col" style={{ width: '30%' }}>Recipient</th>
                                                    <th scope="col" style={{ width: '18%' }}>Mpesa ID</th>
                                                    <th scope="col" style={{ width: '14%' }}>Status</th>
                                                    <th scope="col" style={{ width: '20%' }}>Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {payoutsQuery.status === 'pending' ? (
                                                    <TableLoadingRow colSpan={5} />
                                                ) : payoutsQuery.error ? (
                                                    <TableErrorRow colSpan={5} />
                                                ) : payouts.length === 0 ? (
                                                    <TableEmptyRow colSpan={5} message="No payouts recorded yet." />
                                                ) : (
                                                    payouts.map((item) => {
                                                        const badge = payoutStatusBadge(item.post_mpesa_result_code)
                                                        return (
                                                            <tr key={item.id}>
                                                                <td>
                                                                    <div className="d-lg-none d-flex justify-content-between align-items-start gap-3">
                                                                        <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                                                            <div className="fw-semibold text-nowrap">
                                                                                {item.amount != null
                                                                                    ? formatAmount(item.amount)
                                                                                    : <span className="text-muted fst-italic">Pending</span>}
                                                                            </div>
                                                                            <div className="text-muted small mt-1">
                                                                                To{' '}
                                                                                {item.is_own_payout
                                                                                    ? <CounterpartyReveal payoutId={item.id} maskedValue={item.receiver_party || '-'} payout={item} requiresFetch={item.can_reveal_counterparty} />
                                                                                    : (item.receiver_party ? formatReceiverPartyForDisplay(item.receiver_party) : '-')}
                                                                            </div>
                                                                            <div className="text-muted small">Receipt {item.post_mpesa_receipt_number || '-'}</div>
                                                                            <div className="text-muted small">{formatDate(item.created_at)}</div>
                                                                        </div>
                                                                        <div className="text-end flex-shrink-0">
                                                                            <span className={`badge badge-rounded ${badge.cls}`}>
                                                                                {badge.label}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                    <span className="d-none d-lg-inline text-nowrap fw-semibold">
                                                                        {item.amount != null
                                                                            ? formatAmount(item.amount)
                                                                            : <span className="text-muted fst-italic">Pending</span>}
                                                                    </span>
                                                                </td>
                                                                <td className="d-none d-lg-table-cell" style={{ maxWidth: '200px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                    {item.is_own_payout
                                                                        ? <CounterpartyReveal payoutId={item.id} maskedValue={item.receiver_party || '-'} payout={item} requiresFetch={item.can_reveal_counterparty} />
                                                                        : (item.receiver_party ? formatReceiverPartyForDisplay(item.receiver_party) : '-')}
                                                                </td>
                                                                <td className="d-none d-lg-table-cell text-nowrap">
                                                                    {item.post_mpesa_receipt_number || '-'}
                                                                </td>
                                                                <td className="d-none d-lg-table-cell">
                                                                    <span className={`badge badge-rounded ${badge.cls}`}>
                                                                        {badge.label}
                                                                    </span>
                                                                </td>
                                                                <td className="d-none d-lg-table-cell text-nowrap">{formatDate(item.created_at)}</td>
                                                            </tr>
                                                        )
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                        <PaginationBar
                                            page={payoutsPage}
                                            lastPage={payoutsLast}
                                            isLoading={payoutsQuery.isFetching}
                                            onPrev={() => { setPayoutsPage(p => p - 1); scrollToPageTop(); }}
                                            onNext={() => { setPayoutsPage(p => p + 1); scrollToPageTop(); }}
                                        />
                                    </div>
                                </div>
                            )}

                            {activeTab === TABS.STATEMENT && (
                                <div>
                                    {statementData && (
                                        <div className="mb-2">
                                            <span className="text-muted small d-block">{statementData.account_name}</span>
                                            <span className="fw-bold fs-5">{formatAmount(statementData.current_balance ?? '0')}</span>
                                            <span className="text-muted small ms-1">current balance</span>
                                        </div>
                                    )}
                                    <FilterBar
                                        filters={statementFilters}
                                        showType={true}
                                        onChange={handleStatementFilterChange}
                                        onClear={() => { setStatementFilters(EMPTY_STATEMENT_FILTERS); setStatementPage(1) }}
                                        onExport={handleStatementExport}
                                        hasData={statementEntries.length > 0}
                                    />
                                    <div>
                                        <table className="table verticle-middle mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
                                            <thead>
                                                <tr>
                                                    <th scope="col" className="d-none d-lg-table-cell" style={{ width: '14%' }}>Date</th>
                                                    <th scope="col" style={{ width: '46%' }}>Description</th>
                                                    <th scope="col" className="d-none d-lg-table-cell" style={{ width: '24%' }}>Reference</th>
                                                    <th scope="col" className="d-none d-lg-table-cell text-end" style={{ width: '16%' }}>Amount</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {statementQuery.status === 'pending' ? (
                                                    <TableLoadingRow colSpan={4} />
                                                ) : statementQuery.error ? (
                                                    <TableErrorRow colSpan={4} />
                                                ) : statementEntries.length === 0 ? (
                                                    <TableEmptyRow colSpan={4} message="No entries recorded yet." />
                                                ) : (
                                                    statementEntries.map((entry, index) => (
                                                        <tr key={index}>
                                                            <td className="d-none d-lg-table-cell text-nowrap">{formatDate(entry.date)}</td>
                                                            <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                <div className="d-lg-none d-flex justify-content-between align-items-start gap-3">
                                                                    <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                                                                        <div>{describeStatementEntry(entry)}</div>
                                                                        <div className="text-muted small mt-1" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                            {formatDate(entry.date)}
                                                                            {entry.reference && ` · ${entry.reference}`}
                                                                        </div>
                                                                    </div>
                                                                    <div className={`text-end flex-shrink-0 ${entry.direction === 'in' ? 'text-success' : 'text-danger'}`}>
                                                                        <span className="fw-medium text-nowrap">
                                                                            {entry.direction === 'in' ? '+' : '-'}{formatAmount(entry.total_deducted ?? entry.amount)}
                                                                        </span>
                                                                        {entry.type === 'payout' && (
                                                                            <div className="text-muted" style={{ fontSize: '0.72rem', lineHeight: 1.3, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                                                                                Recipient {formatAmount(entry.amount)}
                                                                                <br />
                                                                                Fee {formatAmount(entry.combined_fee)}
                                                                                {entry.combined_fee_confirmed === false && (
                                                                                    <>
                                                                                        <br />
                                                                                        <span className="fst-italic">Awaiting fee confirmation</span>
                                                                                    </>
                                                                                )}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                                <span className="d-none d-lg-inline">{describeStatementEntry(entry)}</span>
                                                            </td>
                                                            <td className="d-none d-lg-table-cell text-nowrap text-muted small">{entry.reference || '-'}</td>
                                                            <td className={`d-none d-lg-table-cell text-end ${entry.direction === 'in' ? 'text-success' : 'text-danger'}`}>
                                                                <span className="fw-medium text-nowrap">
                                                                    {entry.direction === 'in' ? '+' : '-'}{formatAmount(entry.total_deducted ?? entry.amount)}
                                                                </span>
                                                                {entry.type === 'payout' && (
                                                                    <div className="text-muted" style={{ fontSize: '0.72rem', lineHeight: 1.3, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                                                                        Recipient {formatAmount(entry.amount)}
                                                                        <br />
                                                                        Fee {formatAmount(entry.combined_fee)}
                                                                        {entry.combined_fee_confirmed === false && (
                                                                            <>
                                                                                <br />
                                                                                <span className="fst-italic">Awaiting fee confirmation</span>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                        <PaginationBar
                                            page={statementPage}
                                            lastPage={statementLast}
                                            isLoading={statementQuery.isFetching}
                                            onPrev={() => { setStatementPage(p => p - 1); scrollToPageTop(); }}
                                            onNext={() => { setStatementPage(p => p + 1); scrollToPageTop(); }}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    )
}

export default MyActivity
